/**
 * @file Documents.jsx
 * @description This component renders the detailed profile of a country, including its legislative documents and related information.
 * It fetches data based on the country ISO code from the URL, manages state for profiles, sections, and user interactions,
 * and orchestrates the rendering of the layout, navigation, and content details.
 * The component fully supports multi-language display, adapting content based on the detected language from the URL.
 * @module pages/_Documents/Documents
 * @author FAO
 * @version 1.0.0
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import "./Documents.css";
import Config from "../../config";
import { DownloadPDFSummary } from "../../helper/DownloadPDFSummary";
import { Dropdown } from "primereact/dropdown";
import { Costants } from "../../helper/Costants";

/**
 * Renders the country-specific documents page.
 * It handles data fetching, state management, and UI rendering for the country profile view.
 * @returns {JSX.Element|null} The rendered component or null if no profiles are loaded.
 */
function Documents() {
  // Extracts the 'countryId' from the URL, which is the ISO3 code for the country.
  const { countryId } = useParams();

  // State to hold the filtered profile data for the selected country.
  const [profiles, setProfile] = useState([]);
  // State to hold the full list of country profiles for the dropdown selector.
  const [countryLoad, setCountryLoad] = useState([]);
  // State to manage the currently selected country object in the dropdown.
  const [country, setCountry] = useState([]);
  // State to hold the currently active section's data being displayed.
  const [section, setSection] = useState([]);
  // State to store the title of the currently active section for highlighting.
  const [sectionTitle, setSectionTitle] = useState([]);

  // `useIntl` hook from `react-intl` provides internationalization utilities,
  // such as message formatting, to support multiple languages in the UI.
  const intl = useIntl();

  // --- Data Flow: Language Detection ---
  // The active language for the UI is dynamically determined from the URL path.
  // For example, a URL like /en/country/profiles/USA will extract 'en' as the language.
  var url = window.location.pathname;
  let dateLang = url.split("/")[1];

  // Implements a fallback mechanism: if the extracted language segment is not one of the explicitly
  // supported languages (French, Spanish, Russian, Arabic, Chinese), the system defaults to English.
  if (
    dateLang !== "fr" &&
    dateLang !== "es" &&
    dateLang !== "ru" &&
    dateLang !== "ar" &&
    dateLang !== "zh"
  ) {
    dateLang = "en";
  }
  // The 'language' variable now holds the validated or defaulted language code, used throughout the component
  // for fetching localized content and formatting messages.
  var language = dateLang;

  // Dictionary mapping section titles to descriptive text keys for internationalization.
  const sectionTextboxDict = Costants.allowedSectionEnDescriptiveTextbox;

  // --- Data Flow: Initial Data Fetching ---
  /**
   * Fetches all country profiles on component mount or when the countryId changes.
   * The fetched data is then filtered to find the profile matching the current countryId.
   * It populates the 'profiles' state with the specific country's data,
   * 'countryLoad' with all profiles for the dropdown, and 'country' with the selected country object.
   */
  useEffect(() => {
    fetch(Config.apis.countryprofiles)
      .then((response) => response.json())
      .then((data) => {
        const currentProfile = data.profiles.filter((x) => x.country_ISO3_Code === countryId);
        setProfile(currentProfile);
        setCountryLoad(data.profiles); // For the dropdown
        setCountry(currentProfile); // For the dropdown's value
      });
  }, [countryId]); // Dependency: Refetches if the countryId in the URL changes.

  // --- Data Flow: Section Initialization ---
  /**
   * Sets the initial active section once the country profile data is loaded.
   * It defaults to the first section available in the profile.
   * The `setSectionTitle` also uses the `language` variable to display the section title
   * in the currently active language, falling back to English if the translation is not available.
   * This ensures that the UI always has a section to display by default.
   */
  useEffect(() => {
    if (profiles.length > 0) {
      let initialSection = [];
      initialSection.push(profiles[0].section[0]);
      setSection(initialSection);
      setSectionTitle(initialSection[0][`SectionTitle_${language}`] || initialSection[0].SectionTitle_en);
    }
  }, [profiles, language]); // Dependency: Runs whenever the 'profiles' state is updated.

  // --- Side Effect: Local Storage Update ---
  /**
   * Stores the current country's profile and the active language in local storage.
   * This can be used for persistence or to share data with other parts of the application (e.g., country-snapshot).
   */
  useEffect(() => {
    if (profiles.length > 0) {
      localStorage.setItem("profile", JSON.stringify(profiles[0]));
      localStorage.setItem("language", language);
    }
  }, [profiles, language]); // Dependency: Runs if profile or language changes.

  // --- Computation: PDF Generation Data Preparation ---
  /**
   * Prepares the content for the PDF summary download.
   * It iterates through the profile's sections, subsections, and documents,
   * dynamically selecting titles and names based on the active `language`.
   * This ensures the generated PDF reflects the language currently displayed in the UI.
   * It transforms the data into a structured format ('rowInit' array) that the PDF generation utility can consume.
   * This is a pre-rendering computation that happens only when the user clicks the download button.
   */
  const DownloadPdfSummary = () => {
    let data = profiles[0];
    let rowInit = []; // This array will hold all the structured objects for the PDF.

    if (data.section) {
      data.section.forEach(section => {
        // Add section title, prioritizing the current language or falling back to English.
        rowInit.push({
          text: section[`SectionTitle_${language}`] || section.SectionTitle_en,
          style: "paragraph",
          alignment: "left",
          fontSize: 18,
          color: "#116AAB",
          marginTop: 25,
        });

        // Process subsections
        if (section.subsection) {
          section.subsection.forEach(subsection => {
            // Add subsection title, prioritizing the current language or falling back to English.
            rowInit.push({
              text: subsection[`SubsectionTitle_${language}`] || subsection.SubsectionTitle_en || "",
              style: "paragraph",
              alignment: "left",
              fontSize: 12,
              bold: true,
              color: "#545454",
              marginTop: 12,
            });

            // Process documents within the subsection
            if (subsection.document) {
              subsection.document.forEach(doc => {
                // Document title/name, prioritizing the current language or falling back to English.
                rowInit.push({
                  text: doc[`name_${language}`] || `${doc.Title_of_Text} ${doc.Date_of_Text}` || "",
                  link: doc.id ? `${url}/document/${doc.id}/` : null,
                  style: "paragraph",
                  decoration: doc.id ? "underline" : null,
                  alignment: "left",
                  fontSize: 12,
                  color: "#545454",
                  marginTop: 6,
                });

                // Add natural resources if available
                if (doc.Natural_resources && doc.Natural_resources.length > 0) {
                  rowInit.push({ text: "Natural resources:", bold: true, marginTop: 12, fontSize: 12, color: "#545454" });
                  doc.Natural_resources.forEach(resource => {
                    rowInit.push({ text: resource, fontSize: 12, color: "#545454", marginTop: 6 });
                  });
                }

                // Add environment details if available
                if (doc.Environment_climate_biodiversity && doc.Environment_climate_biodiversity.length > 0) {
                  rowInit.push({ text: "Environment, climate, biodiversity:", bold: true, marginTop: 12, fontSize: 12, color: "#545454" });
                  doc.Environment_climate_biodiversity.forEach(env => {
                    rowInit.push({ text: env, fontSize: 12, color: "#545454", marginTop: 6 });
                  });
                }
              });
            }
          });
        }

        // Process related websites
        if (section.related_websites) {
          section.related_websites.forEach(website => {
            // Website name and URL, prioritizing the current language or falling back to English.
            rowInit.push({
              text: website[`name_${language}`] || website.name_en,
              link: website[`url_${language}`] || website.url_en,
              style: "paragraph",
              decoration: "underline",
              alignment: "left",
              fontSize: 12,
              color: "#545454",
              marginTop: 6,
            });
          });
        }
      });
    }

    // Determine the country's CITES party status for the PDF title, using the localized country name.
    const countryTitle = `${data[`country_${language}`].toString().toLowerCase()} ${data.party_to_CITES ? "(Party to CITES)" : "(Not a Party to CITES)"}`;

    // Call the external utility to generate and download the PDF.
    DownloadPDFSummary(rowInit, countryTitle);
  };

  // --- Event Handler: Country Change ---
  /**
   * Handles the change event from the country dropdown.
   * It triggers a page navigation to the selected country's profile.
   * @param {object} e - The event object from the Dropdown component.
   */
  const changeCountry = (e) => {
    const newCountryCode = e.value.country_ISO3_Code;
    window.location.href = `/${language}/country/profiles/${newCountryCode}`;
  };

  // --- Event Handler: Section Change ---
  /**
   * Handles clicks on the section navigation sidebar.
   * It updates the 'section' state to the newly selected section, causing a re-render of the content area.
   * The `setSectionTitle` function uses the `language` variable to display the section title
   * in the currently active language, falling back to English if the translation is not available.
   * @param {object} newSection - The section object that was clicked.
   */
  const changeSection = (newSection) => {
    if (newSection) {
      let array_temp = [newSection];
      setSection(array_temp);
      setSectionTitle(array_temp[0][`SectionTitle_${language}`] || array_temp[0].SectionTitle_en);
    }
  };

  // --- Rendering Logic ---
  // The component renders only if profile data has been successfully loaded.
  return profiles !== null
    ? profiles.map((item) => (
      // The main container is keyed by the country code to ensure React re-mounts the component on country change.
      <div id="document-cp" key={item.country_ISO3_Code}>
        <header>
          {/* Header navigation: Home, Country List, Country Selector, Advanced Search */}
          <div className="header-country-profile">
            <div className="menu-container d-flex justify-content-start align-items-center">
              <div className="text-header" style={{ marginLeft: 60 }}>
                <a className="link-header" href={"/" + dateLang}>
                  <i
                    className="bi bi-house-door-fill"
                    style={{ height: 31, fontWeight: "bold" }}
                  ></i>
                </a>
              </div>
              <div className="text-header ms-4">
                <a className="link-header" href={"/" + dateLang + "/country"}>
                  <FormattedMessage id="Navigation.country" />
                </a>
              </div>
              <div className="ms-4">
                <Dropdown
                  id="country"
                  className="country-multi"
                  value={country} // The currently displayed country
                  onChange={changeCountry} // Handler for changing country
                  options={countryLoad} // The list of all countries
                  // Dynamically sets the label for each country in the dropdown based on the active language.
                  optionLabel={`country_${language}`}
                  // Sets the placeholder text for the dropdown using internationalized messages.
                  placeholder={intl.formatMessage({ id: "Navigation.select_country" })}
                  filter // Enables filtering in the dropdown
                />
              </div>
              <div className="ms-4">
                <Link to={"/" + dateLang + "/search"} className="text-button">
                  <button className="button-main">
                    <i
                      id="asideToggleIcon"
                      className="bi bi-search inside-icon"
                    ></i>
                    <span className="ms-3">
                      {/* Displays internationalized text for the advanced search button. */}
                      <FormattedMessage id="Navigation.advanced" />
                    </span>
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </header>
        <div className="row">
          {/* --- Pre-render Computation: Sidebar Generation --- */}
          <div className="col-2 nav-mobile">
            {/* Maps over the sections of the country profile to build the side navigation menu. */}
            {item.section.map((section) => {

              // Determines the current section title, prioritizing the active language or falling back to English.
              const sectionTitleCurrentLang = section[`SectionTitle_${language}`] || section?.SectionTitle_en;
              // Excludes the "Related country profiles" section from the main navigation.
              if (section?.SectionTitle_en?.toLowerCase().includes("related country profiles")) {
                return null;
              }

              // Filters subsections to ensure they have a valid title in the current language or English before rendering.
              const subSectionFiltered = section?.subsection?.filter((subdectionObj) =>
                (subdectionObj[`SubsectionTitle_${language}`] || subdectionObj.SubsectionTitle_en)?.length > 0);

              // If there are filtered subsections, render them as a nested list.
              if (subSectionFiltered?.length > 1) {
                return (
                  <div key={sectionTitleCurrentLang}>
                    <div style={{ fontWeight: "bold", fontSize: "16px" }}>{sectionTitleCurrentLang}</div>
                    <ul style={{ marginBottom: "30px" }}>
                      {subSectionFiltered?.map((subsection) => (
                        <li key={subsection.SubsectionTitle_en} className="title-doc-subdection" onClick={(e) => changeSection(
                          // When a subsection is clicked, it reconstructs a section-like object
                          // to be handled by the changeSection function and content rendering logic.
                          // Section titles are dynamically set based on the active language.
                          {
                            "SectionTitle_en": `${section?.SectionTitle_en} (${subsection?.SubsectionTitle_en})`,
                            "SectionTitle_fr": language === "fr" ? `${sectionTitleCurrentLang} (${subsection[`SubsectionTitle_${language}`] || subsection.SubsectionTitle_en})` : null,
                            "SectionTitle_es": language === "es" ? `${sectionTitleCurrentLang} (${subsection[`SubsectionTitle_${language}`] || subsection.SubsectionTitle_en})` : null,
                            "SectionTitle_ru": language === "ru" ? `${sectionTitleCurrentLang} (${subsection[`SubsectionTitle_${language}`] || subsection.SubsectionTitle_en})` : null,
                            "SectionTitle_ar": language === "ar" ? `${sectionTitleCurrentLang} (${subsection[`SubsectionTitle_${language}`] || subsection.SubsectionTitle_en})` : null,
                            "SectionTitle_zh": language === "zh" ? `${sectionTitleCurrentLang} (${subsection[`SubsectionTitle_${language}`] || subsection.SubsectionTitle_en})` : null,
                            subsection: [subsection]
                          }
                        )}>
                          {/* Displays the subsection title, prioritizing the active language or falling back to English. */}
                          {subsection[`SubsectionTitle_${language}`] || subsection.SubsectionTitle_en}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              } else {
                // Otherwise, render a single clickable section title.
                return (
                  <div key={sectionTitleCurrentLang}>
                    <div
                      className={`title-doc ${sectionTitleCurrentLang === sectionTitle
                        ? "highlighted" // Highlight if it's the active section
                        : ""
                        }`}
                      onClick={(e) => changeSection(section)}
                    >
                      <div style={{ fontWeight: "bold", fontSize: "16px" }}>{sectionTitleCurrentLang}</div>
                    </div>
                  </div>
                );
              }
            }
            )}
          </div>
          {/* --- Content Display Area --- */}
          <div className="col-10 mt-3 description">
            <div className="row">
              <div className="col-8">
                {/* Country Title and CITES status */}
                <h2 style={{ border: 0, margin: 0 }}>
                  <span
                    className={`flag flag-${item.country_ISO3_Code
                      .toString()
                      .toLowerCase()}`}
                    style={{ borderRadius: 2 }}
                  ></span>
                  {/* Displays the country name in the currently active language. */}
                  {item["country_" + language]}
                  {item.party_to_CITES ? (
                    <FormattedMessage id="Cites.party" />
                  ) : (
                    <span className="party-cites ms-2">
                      <FormattedMessage id="Cites.not_party" />
                    </span>
                  )}
                </h2>
              </div>
              <div className="col-4">
                {/* Action Buttons: View Snapshot and Download PDF */}
                <div className="input-viewdoc">
                  <Link
                    to={{
                      pathname: `/${dateLang}/country-snapshot/${item.country_ISO3_Code}`,
                      state: section, // Passes the current section data to the snapshot page
                    }}
                    target="_blank"
                    className="links"
                    style={{ textDecoration: "none" }}
                  >
                    <button
                      type="submit"
                      className="btn btn-primary btn-icon d-block mb-3"
                      value={intl.formatMessage({
                        id: "Country_profiles.button",
                      })}
                    >
                      <i className="bi bi-file-earmark-text"></i>
                      <FormattedMessage id="Country_profiles.button" />
                    </button>
                  </Link>
                </div>
                <div className="input-viewdoc">
                  <button
                    type="submit"
                    className="btn btn-primary btn-icon d-block mb-3"
                    onClick={DownloadPdfSummary} // Triggers the PDF generation process
                  >
                    <i className="bi bi-file-earmark-pdf"></i>
                    <FormattedMessage id="Download.pdf" />
                  </button>
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-9">
                {/* Renders the title of the currently active section, localized to the current language. */}
                {section.map((section) => (
                  <div key={section.SectionTitle_en}>
                    <h5 className="title-desc">{section[`SectionTitle_${language}`] || section.SectionTitle_en}</h5>
                  </div>
                ))}
                {
                  // Display a descriptive text box if the current section is configured to have one.
                  sectionTextboxDict[section[0]?.SectionTitle_en] ? (
                    <div className="text-center mt-3 mb-3" hidden={!(section[0]?.SectionTitle_en in sectionTextboxDict)}>
                      <div
                        className="read-only-textbox"
                        dangerouslySetInnerHTML={{
                          __html: intl.formatMessage({ id: `Country_profiles.desciptive_text_box.${sectionTextboxDict[section[0]?.SectionTitle_en]}` })
                        }}
                      />
                    </div>
                  ) : null
                }
                {/* --- Pre-render Computation: Document and Website Listing --- */}
                {/* Renders the content of the active section, which can be documents or websites. */}
                {section[0]?.subsection?.map((subsection) =>
                  // Check if subsection has a title to render it in a separate block
                  (subsection[`SubsectionTitle_${language}`] || subsection.SubsectionTitle_en) ? (
                    <div style={{ marginBottom: 20 }} key={subsection.SubsectionTitle_en}>
                      <div>
                        {subsection.document.length > 0 ? (
                          subsection.document?.map((doc) =>
                            doc.Title_of_Text ? (
                              <div className="mt-2" key={doc.id || doc.Title_of_Text}>
                                <Link
                                  // Constructs the link to the document details page, including the active language.
                                  to={`https://www.fao.org/faolex/results/details/${language}/c/${doc.id}`}
                                  target="_blank"
                                >
                                  {doc.Territorial_Subdivision && (
                                    <span className="me-2">{"[" + doc.Territorial_Subdivision + "]"}</span>
                                  )}
                                  <i className="bi bi-file-earmark-text me-1"></i>
                                  {doc.Title_of_Text || ""}
                                  &nbsp;
                                </Link>
                                {/* Displays localized date information using `intl.formatMessage`. */}
                                {doc.Date_of_Text ? `${intl.formatMessage({ id: "Advanced_search.sorting.date_of_text" })}: ${doc.Date_of_Text}` : ""}
                                {doc.Date_of_Consolidation ? ` | As last amended: (${doc.Date_of_Consolidation})` : ""}
                              </div>
                            ) : (
                              <div className="mt-2" key={doc[`name_${language}`]}>
                                {doc.url ? (
                                  <Link to={`https://${doc.url}`} target="_blank">
                                    <i className="bi bi-file-earmark-text me-1"></i>
                                    {/* Displays the document name, prioritizing the active language or falling back to English. */}
                                    {doc[`name_${language}`] || doc[`name_en`] || ""}
                                  </Link>
                                ) : (
                                  <div>
                                    <i className="bi bi-file-earmark-text me-1"></i>
                                    {/* Displays the document name, prioritizing the active language or falling back to English. */}
                                    {doc[`name_${language}`] || doc[`name_en`] || ""}
                                  </div>
                                )}
                              </div>
                            )
                          )
                        ) : (
                          <div><FormattedMessage id="Document.no_legislation_available" /></div>
                        )}
                      </div>
                      <hr />
                    </div>
                  ) : // If no subsection title, render documents directly
                    subsection.document?.length > 0 ? (
                      <div>
                        {subsection.document?.map((doc) => (
                          <div className="mt-2" key={doc.id || doc.Title_of_Text}>
                            <Link
                              // Constructs the link to the document details page, including the active language.
                              to={`https://www.fao.org/faolex/results/details/${language}/c/${doc.id}`}
                              target="_blank"
                            >
                              {doc.Territorial_Subdivision && (
                                <span className="me-2">{"[" + doc.Territorial_Subdivision + "]"}</span>
                              )}
                              <i className="bi bi-file-earmark-text me-1"></i>
                              {doc.Title_of_Text || ""}
                            </Link>
                            &nbsp;
                            {/* Displays localized date information using `intl.formatMessage`. */}
                            {doc.Date_of_Text ? `${intl.formatMessage({ id: "Advanced_search.sorting.date_of_text" })}: ${doc.Date_of_Text}` : ""}
                            {doc.Date_of_Consolidation ? ` | As last amended: (${doc.Date_of_Consolidation})` : ""}

                            {/* Render Natural Resources and Environment details */}
                            {doc.Natural_resources && doc.Natural_resources.length > 0 && (
                              <div>
                                <hr />
                                <h5 className="mt-3 subsection">{
                                  doc[`Natural_resources_${dateLang}`] 
                                  || doc[`Natural_resources_en`] 
                                  || "Natural resources"
                                }:</h5>
                                {doc.Natural_resources.map((resource) => (
                                  <div key={resource}>{resource}</div>
                                ))}
                                <hr />
                              </div>
                            )}
                            {doc.Environment_climate_biodiversity && doc.Environment_climate_biodiversity.length > 0 && (
                              <div>
                                <h5 className="mt-3 subsection">{
                                  doc[`Environment_climate_biodiversity_${dateLang}`] 
                                  || doc[`Environment_climate_biodiversity_`] 
                                  || "Environment, climate, biodiversity"
                                }:</h5>
                                {doc.Environment_climate_biodiversity.map((environment) => (
                                  <div key={environment}>{environment}</div>
                                ))}
                                <hr />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div><FormattedMessage id="Document.no_legislation_available" /></div>
                    )
                )}
                {/* Renders related websites for the section, if any. */}
                {section[0]?.related_websites?.map((website) => (
                  <div className="mb-2" key={website.url_en}>
                    <Link to={`${website[`url_${language}`] || website.url_en}`} target="_blank">
                      <i className="bi bi-globe me-1"></i>
                      {/* Displays the website name, prioritizing the active language or falling back to English. */}
                      {website[`name_${language}`] || website.name_en || ""}
                    </Link>
                  </div>
                ))}
              </div>
              {/* --- Related Country Profiles Sidebar --- */}
              <div className="col-3">
                <div className="pop-up">
                  {/* Finds and renders the "Related country profiles" section, which is handled separately from the main navigation. */}
                  {item.section.map((section) => {
                    // Determines the section title for the related profiles, prioritizing the active language or falling back to English.
                    const sectionTitleCurrentLang = section[`SectionTitle_${language}`] || section.SectionTitle_en;
                    if (section.SectionTitle_en?.includes("Related country profiles")) {
                      return (
                        <div key={section.SectionTitle_en}>
                          <div className="title-website">
                            {sectionTitleCurrentLang}
                          </div>
                          {section.related_websites?.map((website) => (
                            <div className="mb-2" key={website.url_en}>
                              <Link
                                to={website.url_en}
                                target="_blank"
                              >
                                <i className="bi bi-globe me-1"></i>
                                {/* For related websites, the name is currently displayed in English as per existing data structure. */}
                                {website.name_en}
                              </Link>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ))
    : null; // Render null if there are no profiles to display
}

export default Documents;
